// useDocumentRelatedNodes.js
//
// Live adjacent-node subscription for document child rows (items, payments, …).

import { useEffect, useState } from 'react';

import { subscribeAdjacentNodeKeys } from 'services/nodes/NodeEdgeService';
import { subscribeToNodesByKeys } from 'services/nodes/NodeReadService';

function cleanString(value) {
  return String(value || '').trim();
}

/**
 * Subscribes to nodes edged to a document header node.
 *
 * @param {string|null|undefined} documentNodeKey
 * @param {string|null|undefined} baseKey
 * @returns {object[]}
 */
export default function useDocumentRelatedNodes(documentNodeKey, baseKey) {
  const [adjacentKeysSignature, setAdjacentKeysSignature] = useState('');
  const [neighborNodes, setNeighborNodes] = useState([]);

  const resolvedDocumentKey = cleanString(documentNodeKey);
  const resolvedBaseKey = cleanString(baseKey);

  useEffect(() => {
    if (!resolvedDocumentKey || !resolvedBaseKey) {
      setAdjacentKeysSignature('');
      return undefined;
    }

    return subscribeAdjacentNodeKeys(
      resolvedDocumentKey,
      (keys) => {
        const next = Array.from(
          new Set(
            (Array.isArray(keys) ? keys : [])
              .map((key) => cleanString(key))
              .filter(Boolean),
          ),
        ).sort();
        const signature = next.join('\u0001');
        setAdjacentKeysSignature((prev) => (prev === signature ? prev : signature));
      },
      { baseKey: resolvedBaseKey },
    );
  }, [resolvedDocumentKey, resolvedBaseKey]);

  useEffect(() => {
    if (!adjacentKeysSignature) {
      setNeighborNodes([]);
      return undefined;
    }

    const keys = adjacentKeysSignature.split('\u0001').filter(Boolean);
    if (!keys.length) {
      setNeighborNodes([]);
      return undefined;
    }

    return subscribeToNodesByKeys(keys, (rows) => {
      setNeighborNodes(Array.isArray(rows) ? rows : []);
    });
  }, [adjacentKeysSignature]);

  return neighborNodes;
}
